import {
	getSessionToken,
	isSessionTokenExpired,
	useDescope
} from "@descope/react-sdk";
import axios from "axios";
import jwtDecode from "jwt-decode";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import apiRoutes from "../constants/api-routes";
import pages from "../constants/pages";
import { resetNavigationState } from "../redux/navigationSlice";
import { RootState } from "../redux/store";
import {
	selectUser,
	setDescopeUserId,
	setEmail,
	setFirstName,
	setGroups,
	setIsCoAppSuperAdmin,
	setLastName,
	setOrganizationForceLogin,
	setOrganizationIdentityProvider,
	setOrganizationKillSwitch,
	setOrganizationName,
	setOrganizationSsoAndScim,
	setOrgId,
	setPermissions,
	setPhone,
	setRoles,
	setUserId
} from "../redux/userSlice";

interface JwtPayload {
	tenantId?: string;
}

interface SessionManagerOptions {
	refreshInterval?: number;
}

const useSessionManager = (options: SessionManagerOptions = {}) => {
	const { refreshInterval = 300000 } = options;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const sdk = useDescope();
	const user = useSelector((state: RootState) => selectUser(state));

	const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

	const logoutUser = useCallback(async () => {
		try {
			await sdk.logout();
		} catch (e) {
			console.error("Error during logout:", e);
		}

		dispatch(setUserId(null));
		dispatch(setEmail(null));
		dispatch(setFirstName(null));
		dispatch(setGroups([]));
		dispatch(setLastName(null));
		dispatch(setPermissions([]));
		dispatch(setPhone(null));
		dispatch(setRoles([]));
		dispatch(setOrgId(null));
		dispatch(setIsCoAppSuperAdmin(false));
		dispatch(setOrganizationName(null));
		dispatch(setOrganizationForceLogin(null));
		dispatch(setOrganizationKillSwitch(null));
		dispatch(setOrganizationSsoAndScim(null));
		dispatch(setOrganizationIdentityProvider(null));
		dispatch(setDescopeUserId(null));
		dispatch(resetNavigationState());

		if (refreshIntervalRef.current) {
			clearInterval(refreshIntervalRef.current);
			refreshIntervalRef.current = null;
		}

		navigate(pages.login);
	}, [dispatch, sdk, navigate]);

	const getValidSessionToken = useCallback(async (): Promise<string | null> => {
		let token = getSessionToken();
		if (!token) {
			return null;
		}
		if (!isSessionTokenExpired(token)) {
			return token;
		}
		try {
			await sdk.refresh();
			token = getSessionToken();
			return token && !isSessionTokenExpired(token) ? token : null;
		} catch (error) {
			console.error("Failed to refresh Descope session:", error);
			return null;
		}
	}, [sdk]);

	const refreshUserData = useCallback(async () => {
		if (!user.email) {
			return;
		}

		const sessionToken = await getValidSessionToken();
		if (!sessionToken) {
			logoutUser();
			return;
		}

		try {
			const decodedJwt = jwtDecode<JwtPayload>(sessionToken);
			const tenantId = decodedJwt.tenantId || null;

			const response = await axios.get(
				apiRoutes.getUser(1, "", user.email, tenantId || ""),
				{ headers: { Authorization: "Bearer " + sessionToken } }
			);

			dispatch(setFirstName(response.data.firstname));
			dispatch(setLastName(response.data.lastname));
			dispatch(setGroups(response.data.groups));
			dispatch(setRoles(response.data.roles));
			dispatch(setPermissions(response.data.permissions));
			dispatch(setIsCoAppSuperAdmin(response.data.isCoAppSupport));
			dispatch(setPhone(response.data.phone));
			dispatch(setOrgId(response.data.organizationId));
			dispatch(setOrganizationName(response.data.organization?.name));

			if (response.data.organization) {
				dispatch(setOrganizationForceLogin(response.data.organization.forceLoginActive));
				dispatch(setOrganizationKillSwitch(response.data.organization.killSwitchActive));
				dispatch(setOrganizationSsoAndScim(response.data.organization.isSsoAndScimEnabled));
				if (response.data.organization.isSsoAndScimEnabled) {
					dispatch(setOrganizationIdentityProvider(response.data.organization.identityProviderName));
				}
			}
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 401) {
				logoutUser();
				return;
			}
			console.error("Failed to refresh user profile:", error);
		}
	}, [user.email, dispatch, getValidSessionToken, logoutUser]);

	useEffect(() => {
		if (!user.userId || !user.email) {
			return;
		}

		refreshIntervalRef.current = setInterval(refreshUserData, refreshInterval);

		return () => {
			if (refreshIntervalRef.current) {
				clearInterval(refreshIntervalRef.current);
				refreshIntervalRef.current = null;
			}
		};
	}, [user.userId, user.email, refreshUserData, refreshInterval]);
};

export default useSessionManager;
